import React, { useContext } from 'react';
import {
    Box,
    CircularProgress,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
} from '@mui/material';

import { Message } from '../../../type/chat.types';
import { MessageContext } from '../chat-messages';
import MoreVertIcon from '@mui/icons-material/MoreVert';

type MessageStatusProps = {
    message: Message;
    isMe: boolean;
    hasFile: boolean;
    index: number;
};

const MessageStatus = ({ message, isMe }: MessageStatusProps) => {
    const messageHook = useContext(MessageContext);
    const {
        isSending,
        isMessageError,
        onEdit,
        onDeleteOrRestore,
        userInfo,
    } = messageHook!;

    const isPending = isSending && message?.isNotSent;
    const isFailed = isMessageError && message?.isNotSent;
    const isEdited =
        message?.created_at !== message?.updated_at && !!userInfo?.isAdmin;
    const hasMenu = !!onDeleteOrRestore;

    if (!hasMenu && !isPending && !isFailed && !isEdited) return null;

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: isMe ? 'row-reverse' : 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                minHeight: 24,
                mt: 0.75,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                {onDeleteOrRestore && (
                    <MessageMenu
                        message={message}
                        onEdit={isMe ? onEdit : undefined}
                        onDeleteOrRestore={onDeleteOrRestore}
                    />
                )}
            </Box>

            {isMe && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55 }}>
                    {isPending ? (
                        <CircularProgress size={11} thickness={5} color="inherit" />
                    ) : isFailed ? (
                        <Tooltip title="ارسال ناموفق" arrow>
                            <Box
                                component="span"
                                sx={{ color: 'error.main', display: 'inline-flex' }}>
                                <i
                                    className="fa-light fa-circle-exclamation"
                                    style={{ fontSize: 12 }}
                                />
                            </Box>
                        </Tooltip>
                    ) : null}

                    {isEdited && (
                            <Typography
                                variant="caption"
                                sx={{ fontSize: 10.5, color: 'text.disabled' }}>
                                ویرایش شده
                            </Typography>
                        )}
                </Box>
            )}
        </Box>
    );
};

export default MessageStatus;

export const MessageMenu = ({ message, onEdit, onDeleteOrRestore }: any) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClose = () => setAnchorEl(null);

    return (
        <div>
            <IconButton
                onClick={(event) => setAnchorEl(event.currentTarget)}
                size="small"
                sx={{
                    width: 26,
                    height: 26,
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'action.hover' },
                }}>
                <MoreVertIcon sx={{ width: 17, height: 17 }} />
            </IconButton>

            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                {onEdit && (
                    <MenuItem
                        sx={menuItemSx}
                        onClick={() => {
                            onEdit(message);
                            handleClose();
                        }}>
                        ویرایش
                    </MenuItem>
                )}

                {!message?.deleted_at ? (
                    <MenuItem
                        sx={menuItemSx}
                        onClick={() => {
                            onDeleteOrRestore(message?.id, 'delete');
                            handleClose();
                        }}>
                        حذف
                    </MenuItem>
                ) : (
                    <MenuItem
                        sx={menuItemSx}
                        onClick={() => {
                            onDeleteOrRestore(message?.id, 'restore');
                            handleClose();
                        }}>
                        بازگردانی
                    </MenuItem>
                )}
            </Menu>
        </div>
    );
};

const menuItemSx = { fontSize: 12, py: 0.4, px: 1.25, minHeight: 30 };
